import * as THREE from 'three'
import type { GSplatInfo } from '@/plugins/gsplat/lod/GSplatInfo'
import {
  DEFAULT_CENTERS_PICK_POINT_SIZE,
  DEFAULT_MAX_PICK_RESULTS,
  DEFAULT_RECT_PICK_MAX_RESULTS,
} from '@/shared/constants'
import { isPickPointAtOrBeyondMinDistance } from '@/shared/utils/pickDepth'

/**
 * 中心点拾取结果
 */
export interface CentersPickResult {
  /** 被拾取的 GSplatInfo 对象 */
  splatInfo: GSplatInfo
  /** splat 在 GSplatInfo 中的索引 */
  splatIndex: number
  /** 屏幕空间距离（像素） */
  screenDistance: number
  /** 世界空间位置 */
  worldPosition: THREE.Vector3
  /** 深度值（用于排序） */
  depth: number
}

/** 中心点候选在截断前使用的可见性谓词。 */
export type CentersPickPredicate = (result: CentersPickResult) => boolean

/**
 * 中心点拾取选项
 */
export interface CentersPickOptions {
  /** 最大返回结果数量，默认 100 */
  maxResults?: number
  /** 点大小（像素），默认 2.0 */
  pointSize?: number
  /** 是否只返回最近的结果，默认 false */
  closestOnly?: boolean
  /** Pick viewport width in CSS pixels. */
  screenWidth?: number
  /** Pick viewport height in CSS pixels. */
  screenHeight?: number
  /** 从当前屏幕射线起点沿归一化方向计算的最小中心距离。 */
  minDistance?: number
}

/**
 * 中心点拾取处理器
 *
 * 使用 CPU 投影计算进行屏幕空间点击选择。
 * 专门用于中心点渲染模式下的 splat 选择。
 *
 * @remarks
 * 核心流程：
 * 1. 计算 VP 矩阵（视图投影矩阵）
 * 2. 遍历所有 GSplatInfo 的中心位置
 * 3. 将世界坐标投影到屏幕坐标
 * 4. 比较屏幕距离，筛选在点大小范围内的 splat
 * 5. 按深度排序返回结果
 *
 * @example
 * ```typescript
 * const results = CentersPickHandler.pickAtScreen(
 *   event.clientX,
 *   event.clientY,
 *   canvas.width,
 *   canvas.height,
 *   camera,
 *   splatInfos,
 *   { pointSize: 4.0 }
 * );
 * ```
 */
export class CentersPickHandler {
  // 可复用的临时变量
  private static _tempVec4 = new THREE.Vector4()
  private static _tempVec3 = new THREE.Vector3()
  private static _tempMatrix = new THREE.Matrix4()
  private static _vpMatrix = new THREE.Matrix4()

  /**
   * 在屏幕坐标处拾取 splat 中心点
   *
   * @param screenX - 屏幕 X 坐标（像素）
   * @param screenY - 屏幕 Y 坐标（像素）
   * @param screenWidth - 屏幕宽度（像素）
   * @param screenHeight - 屏幕高度（像素）
   * @param camera - 相机
   * @param splatInfos - 要检测的 GSplatInfo 数组
   * @param options - 拾取选项
   * @returns 按深度排序的拾取结果数组
   */
  static pickAtScreen(
    screenX: number,
    screenY: number,
    screenWidth: number,
    screenHeight: number,
    camera: THREE.Camera,
    splatInfos: GSplatInfo[],
    options?: CentersPickOptions,
    accept?: CentersPickPredicate
  ): CentersPickResult[] {
    // 解析选项
    const maxResults = options?.maxResults ?? DEFAULT_MAX_PICK_RESULTS
    const pointSize = options?.pointSize ?? DEFAULT_CENTERS_PICK_POINT_SIZE
    const closestOnly = options?.closestOnly ?? false

    // 计算 VP 矩阵（视图投影矩阵）
    const vpMatrix = CentersPickHandler._vpMatrix
    vpMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)

    const halfPointSize = pointSize * 0.5
    let minDistanceRay: THREE.Ray | null = null
    if (options?.minDistance !== undefined) {
      const raycaster = new THREE.Raycaster()
      raycaster.setFromCamera(
        new THREE.Vector2((screenX / screenWidth) * 2 - 1, -(screenY / screenHeight) * 2 + 1),
        camera
      )
      minDistanceRay = raycaster.ray.clone()
    }
    const acceptsResult = (result: CentersPickResult): boolean =>
      (!minDistanceRay ||
        isPickPointAtOrBeyondMinDistance(
          minDistanceRay,
          result.worldPosition,
          options?.minDistance
        )) &&
      (!accept || accept(result))

    // closestOnly 快速路径：只追踪最近结果，不收集+排序
    if (closestOnly) {
      let best: CentersPickResult | null = null
      for (const splatInfo of splatInfos) {
        const resource = splatInfo.resource
        const centers = resource.centers
        if (!centers || centers.length === 0) {
          continue
        }

        const mvpMatrix = CentersPickHandler._tempMatrix
        mvpMatrix.multiplyMatrices(vpMatrix, splatInfo.node.matrixWorld)
        const numSplats = splatInfo.activeSplats > 0 ? splatInfo.activeSplats : splatInfo.numSplats
        const intervals = splatInfo.intervals

        const scan = (start: number, end: number) => {
          for (let idx = start; idx < end; idx++) {
            const r = CentersPickHandler.testSplatAtScreen(
              idx,
              centers,
              mvpMatrix,
              screenX,
              screenY,
              screenWidth,
              screenHeight,
              halfPointSize,
              splatInfo
            )
            if (r && acceptsResult(r) && (!best || r.depth < best.depth)) {
              best = r
            }
          }
        }

        if (intervals.length > 0) {
          for (let i = 0; i < intervals.length; i += 2) {
            scan(intervals[i], intervals[i + 1])
          }
        } else {
          scan(0, numSplats)
        }
      }
      return best ? [best] : []
    }

    // 多结果路径
    const results: CentersPickResult[] = []

    for (const splatInfo of splatInfos) {
      const resource = splatInfo.resource
      const centers = resource.centers
      if (!centers || centers.length === 0) {
        continue
      }

      const mvpMatrix = CentersPickHandler._tempMatrix
      mvpMatrix.multiplyMatrices(vpMatrix, splatInfo.node.matrixWorld)
      const numSplats = splatInfo.activeSplats > 0 ? splatInfo.activeSplats : splatInfo.numSplats
      const intervals = splatInfo.intervals

      if (intervals.length > 0) {
        for (let i = 0; i < intervals.length; i += 2) {
          for (let splatIndex = intervals[i]; splatIndex < intervals[i + 1]; splatIndex++) {
            const result = CentersPickHandler.testSplatAtScreen(
              splatIndex,
              centers,
              mvpMatrix,
              screenX,
              screenY,
              screenWidth,
              screenHeight,
              halfPointSize,
              splatInfo
            )
            if (result && acceptsResult(result)) {
              results.push(result)
            }
          }
        }
      } else {
        for (let splatIndex = 0; splatIndex < numSplats; splatIndex++) {
          const result = CentersPickHandler.testSplatAtScreen(
            splatIndex,
            centers,
            mvpMatrix,
            screenX,
            screenY,
            screenWidth,
            screenHeight,
            halfPointSize,
            splatInfo
          )
          if (result && acceptsResult(result)) {
            results.push(result)
          }
        }
      }
    }

    results.sort((a, b) => a.depth - b.depth)
    return results.slice(0, maxResults)
  }

  /**
   * 测试单个 splat 是否在屏幕坐标范围内
   *
   * @param splatIndex - splat 索引
   * @param centers - 中心位置数组 (Float32Array, 每 3 个为一组 xyz)
   * @param mvpMatrix - MVP 矩阵
   * @param screenX - 屏幕 X 坐标
   * @param screenY - 屏幕 Y 坐标
   * @param screenWidth - 屏幕宽度
   * @param screenHeight - 屏幕高度
   * @param halfPointSize - 点大小的一半
   * @param splatInfo - 所属的 GSplatInfo
   * @returns 拾取结果，如果不在范围内则返回 null
   */
  private static testSplatAtScreen(
    splatIndex: number,
    centers: Float32Array,
    mvpMatrix: THREE.Matrix4,
    screenX: number,
    screenY: number,
    screenWidth: number,
    screenHeight: number,
    halfPointSize: number,
    splatInfo: GSplatInfo
  ): CentersPickResult | null {
    // 获取本地空间中心位置
    const baseIndex = splatIndex * 3
    if (baseIndex + 2 >= centers.length) {
      return null
    }

    const localX = centers[baseIndex]
    const localY = centers[baseIndex + 1]
    const localZ = centers[baseIndex + 2]

    // 投影到裁剪空间
    const clipPos = CentersPickHandler._tempVec4
    clipPos.set(localX, localY, localZ, 1.0)
    clipPos.applyMatrix4(mvpMatrix)

    // 检查是否在视锥体内
    const w = clipPos.w
    if (w <= 0) {
      return null // 在相机后面
    }

    // NDC 坐标
    const ndcX = clipPos.x / w
    const ndcY = clipPos.y / w
    const ndcZ = clipPos.z / w

    // 检查是否在 NDC 范围内
    if (ndcX < -1 || ndcX > 1 || ndcY < -1 || ndcY > 1 || ndcZ < -1 || ndcZ > 1) {
      return null // 在视锥体外
    }

    // 转换到屏幕坐标
    const projectedX = (ndcX * 0.5 + 0.5) * screenWidth
    const projectedY = (-ndcY * 0.5 + 0.5) * screenHeight // Y 轴翻转

    // 计算屏幕距离
    const dx = projectedX - screenX
    const dy = projectedY - screenY
    const screenDistance = Math.sqrt(dx * dx + dy * dy)

    // 检查是否在点大小范围内
    if (screenDistance > halfPointSize) {
      return null
    }

    // 计算世界空间位置
    const worldPos = CentersPickHandler._tempVec3.set(localX, localY, localZ)
    worldPos.applyMatrix4(splatInfo.node.matrixWorld)

    return {
      splatInfo,
      splatIndex,
      screenDistance,
      worldPosition: worldPos.clone(),
      depth: ndcZ,
    }
  }

  /**
   * 批量拾取：在矩形区域内选择所有 splat
   *
   * @param startX - 起始 X 坐标（像素）
   * @param startY - 起始 Y 坐标（像素）
   * @param endX - 结束 X 坐标（像素）
   * @param endY - 结束 Y 坐标（像素）
   * @param screenWidth - 屏幕宽度（像素）
   * @param screenHeight - 屏幕高度（像素）
   * @param camera - 相机
   * @param splatInfos - 要检测的 GSplatInfo 数组
   * @param maxResults - 最大返回结果数量，默认 10000
   * @returns 在区域内的拾取结果数组
   */
  static pickInRect(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    screenWidth: number,
    screenHeight: number,
    camera: THREE.Camera,
    splatInfos: GSplatInfo[],
    maxResults: number = DEFAULT_RECT_PICK_MAX_RESULTS,
    accept?: CentersPickPredicate
  ): CentersPickResult[] {
    // 确保坐标顺序正确
    const minX = Math.min(startX, endX)
    const maxX = Math.max(startX, endX)
    const minY = Math.min(startY, endY)
    const maxY = Math.max(startY, endY)

    // 计算 VP 矩阵
    const vpMatrix = CentersPickHandler._vpMatrix
    vpMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)

    const results: CentersPickResult[] = []

    // 遍历所有 GSplatInfo
    for (const splatInfo of splatInfos) {
      const resource = splatInfo.resource
      const centers = resource.centers

      if (!centers || centers.length === 0) {
        continue
      }

      // 计算 MVP 矩阵
      const mvpMatrix = CentersPickHandler._tempMatrix
      mvpMatrix.multiplyMatrices(vpMatrix, splatInfo.node.matrixWorld)

      const numSplats = splatInfo.activeSplats > 0 ? splatInfo.activeSplats : splatInfo.numSplats
      const intervals = splatInfo.intervals

      // 遍历 splats
      const processIndex = (splatIndex: number) => {
        const baseIndex = splatIndex * 3
        if (baseIndex + 2 >= centers.length) return

        const localX = centers[baseIndex]
        const localY = centers[baseIndex + 1]
        const localZ = centers[baseIndex + 2]

        // 投影到裁剪空间
        const clipPos = CentersPickHandler._tempVec4
        clipPos.set(localX, localY, localZ, 1.0)
        clipPos.applyMatrix4(mvpMatrix)

        const w = clipPos.w
        if (w <= 0) return

        // NDC 坐标
        const ndcX = clipPos.x / w
        const ndcY = clipPos.y / w
        const ndcZ = clipPos.z / w

        if (ndcX < -1 || ndcX > 1 || ndcY < -1 || ndcY > 1 || ndcZ < -1 || ndcZ > 1) {
          return
        }

        // 屏幕坐标
        const projectedX = (ndcX * 0.5 + 0.5) * screenWidth
        const projectedY = (-ndcY * 0.5 + 0.5) * screenHeight

        // 检查是否在矩形内
        if (projectedX >= minX && projectedX <= maxX && projectedY >= minY && projectedY <= maxY) {
          const worldPos = CentersPickHandler._tempVec3.set(localX, localY, localZ)
          worldPos.applyMatrix4(splatInfo.node.matrixWorld)

          const result: CentersPickResult = {
            splatInfo,
            splatIndex,
            screenDistance: 0,
            worldPosition: worldPos.clone(),
            depth: ndcZ,
          }
          if (!accept || accept(result)) {
            results.push(result)
          }
        }
      }

      if (intervals.length > 0) {
        for (let i = 0; i < intervals.length; i += 2) {
          for (let splatIndex = intervals[i]; splatIndex < intervals[i + 1]; splatIndex++) {
            if (results.length >= maxResults) break
            processIndex(splatIndex)
          }
          if (results.length >= maxResults) break
        }
      } else {
        for (let splatIndex = 0; splatIndex < numSplats; splatIndex++) {
          if (results.length >= maxResults) break
          processIndex(splatIndex)
        }
      }

      if (results.length >= maxResults) break
    }

    return results
  }
}
